import { useEffect } from 'react';

import { getCountryConfig, getCountryConfigByPlanGroup } from '../../../constants/countryConfig';
import { getSmsStartingPriceFromAddOns } from '../../../utils/getSmsStartingPriceFromAddOns';
import { getBronzeId, getCurrencySymbol, getPremiumId } from '../../../utils/pricingUtils';
import { PLAN_ID_TO_KEY } from '../../ComparisonTable/constants';
import MyAccountApi from '../api/MyAccountApi';
import { useUpgradeContext } from '../UpgradeContext';

const { getPlanRating, getAddOns } = MyAccountApi;

const cache = {};

const TIER_DISPLAY_NAMES = {
  lite: 'Lite',
  standard: 'Standard',
  premium: 'Premium',
};

const formatPlanName = (iName) => iName.replace('Constant Contact - ', '');

async function memoGetPlanRating(props) {
  const selectedPlanId = props.selectedPlan.id;

  if (!cache[selectedPlanId]) {
    cache[selectedPlanId] = await getPlanRating(props);
  }

  return cache[selectedPlanId];
}

const useInitPlanBlocks = () => {
  const [
    {
      upgradeInfo,
      upgradeContactCount,
      upgradeFormattedCustomerInfo,
      upgradeCurrency,
      selectedPlanIdFromCompare,
      recommendedUpgradePlanPromotion,
      isStripeAccount,
      promoCode,
    },
    dispatchUpgrade,
  ] = useUpgradeContext();

  useEffect(() => {
    if (upgradeContactCount == null || !upgradeFormattedCustomerInfo || !upgradeCurrency || !upgradeInfo) {
      return;
    }

    const getPromoCredit = (rating) => {
      const { promotionData } = rating || {};
      const [includedPromo] = promotionData ? Object.values(promotionData) : [];

      return includedPromo?.promotionCredit ?? 0;
    };

    const {
      currentPlanId,
      paymentPlanId,
      recommendedPlanId,
      recommendedPlanGroupId,
      currentPlanGroupId: planGroupId,
      currentPlanDescription,
      recommendedPlanDescription,
      promotionCode,
    } = upgradeInfo;

    const makeRatingCall = (paramPlanGroupId, selectedPlanId, existingAddOnServices = null, isStripeAccount = false) =>
      memoGetPlanRating({
        paramPlanGroupId,
        selectedPlan: { id: selectedPlanId },
        existingAddOnServices,

        newAddOnServices: [],
        selectedCurrency: upgradeCurrency,
        contactUsed: upgradeContactCount || 0,
        paymentPlanGroupId: paymentPlanId,
        currentPlanId,
        discountCode: promoCode || promotionCode,
        customer: upgradeFormattedCustomerInfo,
        paymentPeriod: 0,
        isStripeAccount,
      });

    (async () => {
      try {
        const bronzePlanId = getBronzeId(recommendedPlanGroupId);
        const countryConfig =
          getCountryConfigByPlanGroup(recommendedPlanGroupId) ?? getCountryConfig(upgradeFormattedCustomerInfo.country);

        const [addOnsResult, smsPricingAddOnsResult] = await Promise.allSettled([
          getAddOns(upgradeCurrency, currentPlanId),
          getAddOns(upgradeCurrency, bronzePlanId),
        ]);

        const addOns = addOnsResult.status === 'fulfilled' ? addOnsResult.value : null;
        const smsPricingAddOns = smsPricingAddOnsResult.status === 'fulfilled' ? smsPricingAddOnsResult.value : null;
        const smsStartingPrice = getSmsStartingPriceFromAddOns(smsPricingAddOns);

        const currentTier = PLAN_ID_TO_KEY[currentPlanId];
        const recommendedTier = PLAN_ID_TO_KEY[recommendedPlanId];
        let comparePlanId = recommendedPlanId;

        if (currentTier && recommendedTier && currentTier === recommendedTier) {
          comparePlanId = getPremiumId(recommendedPlanGroupId);
        }

        const shouldAddComparePlan = Number(comparePlanId) !== Number(currentPlanId);

        const [upgradeRating, currentPlanRating, comparePlanRating] = await Promise.all([
          makeRatingCall(
            recommendedPlanGroupId,
            selectedPlanIdFromCompare || recommendedPlanId,
            addOns?.length ? addOns.filter((addon) => addon.purchased).map((addon) => addon.id) : null,
            isStripeAccount
          ),
          makeRatingCall(planGroupId, currentPlanId, null, isStripeAccount),
          shouldAddComparePlan
            ? makeRatingCall(recommendedPlanGroupId, comparePlanId, null, isStripeAccount)
            : Promise.resolve(null),
        ]);

        dispatchUpgrade({ type: 'setPlanId', currentPlanId });
        dispatchUpgrade({ type: 'setPaymentPlanGroupId', paymentPlanGroupId: paymentPlanId });
        dispatchUpgrade({
          type: 'setUpgradeRating',
          value: upgradeRating,
        });
        dispatchUpgrade({ type: 'setAllAddOns', allAddOns: addOns });
        dispatchUpgrade({ type: 'setSmsStartingPrice', smsStartingPrice });

        const planBlocks = [];

        const currentDiscount = getPromoCredit(currentPlanRating);
        const compareDiscount = comparePlanRating ? getPromoCredit(comparePlanRating) : 0;

        planBlocks.push({
          planBlockInfo: {
            planName: formatPlanName(currentPlanDescription),
            planCurrency: getCurrencySymbol(upgradeCurrency),
            planPrice: currentPlanRating.monthlyPrice - currentDiscount,
            planId: currentPlanId,
            'data-qe-id': 'current-plan-block',
            discount: currentPlanRating.prepayMonthlyDiscountAmount
              ? currentPlanRating.prepayMonthlyDiscountAmount + currentPlanRating.monthlyPrice
              : null,
            isCurrentPlan: true,
          },
        });

        if (shouldAddComparePlan && comparePlanRating) {
          let discountComparePlan = null;

          if (comparePlanRating.promotionData) {
            discountComparePlan = comparePlanRating.monthlyPrice || null;
          }

          if (comparePlanRating.prepayMonthlyDiscountAmount) {
            discountComparePlan = (discountComparePlan || 0) + comparePlanRating.prepayMonthlyDiscountAmount;
          }

          const comparePlanTier = PLAN_ID_TO_KEY[comparePlanId];
          const comparePlanName =
            currentTier === recommendedTier && comparePlanTier
              ? TIER_DISPLAY_NAMES[comparePlanTier] ?? formatPlanName(recommendedPlanDescription)
              : formatPlanName(recommendedPlanDescription);

          planBlocks.push({
            planBlockInfo: {
              planName: comparePlanName,
              planCurrency: getCurrencySymbol(upgradeCurrency),
              planPrice: comparePlanRating.monthlyPrice - compareDiscount / (upgradeRating?.paymentPeriod || 1),
              planId: comparePlanId,
              'data-qe-id': 'upgrade-plan-block',
              discount: discountComparePlan,
              recommended: true,
            },
          });
        }

        const selectedPlanName = planBlocks.find(
          ({ planBlockInfo }) => planBlockInfo.planId === selectedPlanIdFromCompare
        )?.planBlockInfo.planName;

        if (selectedPlanName) {
          dispatchUpgrade({ type: 'setPlanNameDescriptionOnly', value: selectedPlanName });
        }

        dispatchUpgrade({ type: 'setComparePlanBlocks', value: planBlocks });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('useInitPlanBlocks failed', e);
      }
    })();
  }, [
    recommendedUpgradePlanPromotion,
    upgradeContactCount,
    upgradeFormattedCustomerInfo,
    upgradeCurrency,
    upgradeInfo,
    selectedPlanIdFromCompare,
    isStripeAccount,
    promoCode,
    dispatchUpgrade,
  ]);
};

export default useInitPlanBlocks;
